import React, { useEffect, useState } from "react";
import { Box, Tabs, Tab, Grid, Typography, Button, Divider, Modal, CircularProgress, TextField, InputAdornment, Skeleton, Link } from "@mui/material";
import Stack from "@mui/material/Stack";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { setErrorMessage, setSuccessMessage } from "../../redux/actions/appActions";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import { ADMIN_USER_VIDEO_ENHANCEMENT_RETRY_READ, READ_USER_VIDEOS } from "../../redux/api/config";
import { getApplicantDetails, updateJobAction, updateVideoAction } from "../../redux/actions/b2bActions";
import { SET_APPLICANT_DETAILS, SET_APPLICANT_DETAILS_LOADING } from "../../redux/actions/actionTypes";
import moment from "moment";
import VideoThumbnail from "../common/VideoThumbnail";
import ReactPlayer from "react-player";
import B2BHeader from "./B2BHeader";
import { ROUTE_PERSONALITY_PROFILER } from "../../constants/routes";
import { ArrowForward, CheckCircle, Cancel, Schedule } from "@mui/icons-material";
import { useDebunce } from "../common/Debunce";

const getVideoStatusDisplay = (el: any) => {
  const status = (el?.approvalStatus || "").toUpperCase();
  if (status === "APPROVED") return { label: "Approved", color: "#2E7D32", bg: "rgba(76, 175, 80, 0.15)", Icon: CheckCircle };
  if (status === "REJECTED") return { label: "Rejected", color: "#C62828", bg: "rgba(244, 67, 54, 0.12)", Icon: Cancel };
  return { label: "Pending", color: "#E65100", bg: "rgba(255, 152, 0, 0.12)", Icon: Schedule };
};

const styles = {
  subText: {
    fontSize: "16px",
    color: "#777777",
    fontWeight: "400",
  },
  text: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#000000",
  },
  tabStyle: {
    fontWeight: "bold",
  },
  indicator: {
    backgroundColor: "#756de9 !important",
    height: "4px",
  },
  modalContent: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    backgroundColor: "white",
    borderRadius: "10px",
    padding: "20px",
    boxShadow: 24,
  },

  modalContentDecline: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "300px",
    backgroundColor: "white",
    borderRadius: "10px",
    padding: "20px",
    boxShadow: 24,
  },
};

const VideoDetails = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { applicantDetails, applicantDetailsLoading } = useSelector((state: any) => state.b2b);
  const [hasMoreApplicants, setHasMoreApplicants] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedApplicantDetails, setSelectedApplicantDetails] = useState<any>(null);
  const [applicants, setApplicants] = useState<any>([]);
  const [currentApplicantsPage, setCurrentApplicantsPage] = useState(1);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [activeTab1, setActiveTab1] = useState(0);
  const [currentActive, setCurrentActive] = useState(0);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openRetryEnhancementModal, setOpenRetryEnhancementModal] = useState(false);
  const [isVideoActionLoading, setIsVideoActionLoading] = useState(false);
  const [isRetryEnhancementLoading, setIsRetryEnhancementLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebunce(searchTerm, 300);
  const [applicantsSectionTab, setApplicantsSectionTab] = useState(0); // 0 = Pending, 1 = Approved
  const { userDetail, educationDetails, profileDetail, userCertificates, userExperience, userPersonality, userVideos } =
    applicantDetails;
  const [currentQuestionId, setCurrentQuestionId] = useState(-1);

  const { fullName, profileImage, dob, city, phoneNumber, email } = userDetail || {};
  // const { videoUploaded, videoStatus } = profileStatus || {};
  const { languages, skills, tools, workLinks, work_links: workLinksSnake, workLink, work_link: workLinkSnake } =
    profileDetail || {};
  const resolvedWorkLinks: string[] = (() => {
    const fromArray = workLinks ?? workLinksSnake;
    if (Array.isArray(fromArray)) return fromArray.map((v: any) => String(v ?? "").trim()).filter(Boolean);
    const fromSingle = workLink ?? workLinkSnake;
    return fromSingle != null && String(fromSingle).trim() !== "" ? [String(fromSingle).trim()] : [];
  })();
  const formatWorkLinkHref = (raw: string) => {
    const t = String(raw).trim();
    if (!t) return "#";
    if (/^https?:\/\//i.test(t)) return t;
    return `https://${t}`;
  };

  /** Backend field names for per-role work links vary; normalize before rendering. */
  const pickExperienceWorkLink = (el: any): string => {
    const scalars = [
      el?.workLink,
      el?.work_link,
      el?.worklink,
      el?.WorkLink,
      el?.link,
      el?.portfolioLink,
      el?.portfolio_link,
      el?.workUrl,
      el?.work_url,
      el?.website,
      el?.url,
    ];
    for (const v of scalars) {
      const t = v == null ? "" : String(v).trim();
      if (t) return t;
    }
    const arr = el?.workLinks ?? el?.work_links;
    if (Array.isArray(arr)) {
      const first = arr.map((x: any) => (x == null ? "" : String(x).trim())).find((s: string) => s.length > 0);
      if (first) return first;
    }
    return "";
  };

  const handleSelectApplicant = (item: any, details: any) => {
    dispatch({ type: SET_APPLICANT_DETAILS_LOADING, payload: true });
    setSelectedApplicant(item);
    setSelectedApplicantDetails(details);
  };

  const handleVideoClick = (index: any, questionId: any) => {
    setCurrentActive(index);
    setCurrentQuestionId(questionId);
  };

  const fetchApplicants = (
    flag: boolean,
    resetOffset: boolean,
    jobAction = false,
    searchQuery = "",
    videoStatusFilter?: "PENDING" | "APPROVED"
  ) => {
    if (!flag) {
      setLoadingApplicants(true);
    }
    const filters: any = {};
    if (videoStatusFilter) {
      filters.videoStatus = videoStatusFilter;
    }

    const payload = {
      searchQuery: searchQuery || undefined,
      page: jobAction ? currentApplicantsPage : resetOffset ? 1 : currentApplicantsPage + 1,
      fetchSize: 10,
      filters,
    };
    API_MIDDLEWARE(READ_USER_VIDEOS, "POST", { "Content-Type": "application/json" }, payload)
      .then((res: any) => {
        const { records, hasNext, page } = res?.result || {};
        const jobs: any = resetOffset ? [...records] : [...applicants, ...records];
        setApplicants(jobs);
        if(page === 1) {
          setSelectedApplicant(jobs?.[0]?.userId);
          setSelectedApplicantDetails(jobs?.[0]);
        }
        setCurrentApplicantsPage(page);
        setHasMoreApplicants(hasNext);
        setLoadingApplicants(false);
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        setLoadingApplicants(false);
      });
  };

  // Resolve video status filter from tab: 0 = Pending, 1 = Approved
  const videoStatusFilter: "PENDING" | "APPROVED" = applicantsSectionTab === 0 ? "PENDING" : "APPROVED";

  // Fetch applicants when search term, tab (status), or initial mount
  useEffect(() => {
    setCurrentApplicantsPage(1);
    setApplicants([]);
    fetchApplicants(false, true, false, debouncedSearchTerm, videoStatusFilter);
  }, [debouncedSearchTerm, applicantsSectionTab]);

  useEffect(() => {
    if (selectedApplicant) {
      dispatch(getApplicantDetails(selectedApplicant));
    }
  }, [selectedApplicant]);

  useEffect(() => {
    setCurrentQuestionId(userVideos?.[0]?.questionId);
  }, [userVideos]);

  const handleTabChange1 = (event: any, newValue: any) => {
    setActiveTab1(newValue);
  };

  // State for handling the modal
  const [openModal, setOpenModal] = useState(false);
  const [openModalDecline, setOpenModalDecline] = useState(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleOpenModalDecline = () => setOpenModalDecline(true);
  const handleCloseModalDecline = () => setOpenModalDecline(false);

  const handleJobAction = (jobStatus: any) => {
    dispatch(
      updateJobAction(jobStatus, selectedApplicantDetails?.applicationId, () => {
        if (jobStatus === "ACCEPTED") {
          handleCloseModal();
        } else {
          handleCloseModalDecline();
        }
        fetchApplicants(false, true, false, debouncedSearchTerm, videoStatusFilter);
      })
    );
  };

  const calculateYears = (startDate: string, endDate?: string): string => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(); // Use current date if endDate is not provided

    let years = end.getFullYear() - start.getFullYear();
    const additionalMonths = end.getMonth() - start.getMonth();

    // Adjust years if the end month is earlier than the start month
    if (additionalMonths < 0 || (additionalMonths === 0 && end.getDate() < start.getDate())) {
      years -= 1;
    }

    return `${years} yrs`;
  };

  const formatDuration = (duration: string): string => {
    if (!duration) return ""; // Handle empty or invalid values

    const [value, unit] = duration.split(" - ");

    if (!value || !unit) return duration; // Return the original string if improperly formatted

    const formattedUnit =
      parseInt(value) > 1 && !unit.toLowerCase().endsWith("s")
        ? `${unit}s` // Pluralize by adding "s" if not already plural
        : unit;

    return `${value} ${formattedUnit}`;
  };

  const toggleApproveModal = () => setOpenApproveModal(!openApproveModal);
  const toggleRejectModal = () => setOpenRejectModal(!openRejectModal);
  const toggleRetryEnhancementModal = () => {
    if (!isRetryEnhancementLoading) setOpenRetryEnhancementModal(!openRetryEnhancementModal);
  };

  const handleConfirmRetryVideoEnhancement = () => {
    if (!phoneNumber) {
      dispatch(setErrorMessage("Phone number is missing."));
      return;
    }
    setIsRetryEnhancementLoading(true);
    API_MIDDLEWARE(ADMIN_USER_VIDEO_ENHANCEMENT_RETRY_READ(phoneNumber), "GET", { "Content-Type": "application/json" })
      .then((res: any) => {
        dispatch(setSuccessMessage(res?.message ?? "Video enhancement retry requested."));
        setOpenRetryEnhancementModal(false);
        if (selectedApplicant) dispatch(getApplicantDetails(selectedApplicant));
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
      })
      .finally(() => setIsRetryEnhancementLoading(false));
  };

  // Helper function to determine overall video status based on all videos
  const determineOverallVideoStatus = (videos: any[]): string => {
    if (!videos || videos.length === 0) return "PENDING";
    
    const allApproved = videos.every(video => video.approvalStatus === "APPROVED");
    
    if (allApproved) return "APPROVED";
    return "PENDING";
  };

  // Common method to update a specific applicant in the applicants array
  const updateApplicantStatus = (userId: string, updates: any) => {
    setApplicants((prevApplicants: any[]) => {
      const applicantIndex = prevApplicants.findIndex(applicant => applicant.userId === userId);
      if (applicantIndex !== -1) {
        const updatedApplicants = [...prevApplicants];
        updatedApplicants[applicantIndex] = { ...updatedApplicants[applicantIndex], ...updates };
        return updatedApplicants;
      }
      return prevApplicants;
    });
  };

  const handleVideoModal = (status: string) => {
    setIsVideoActionLoading(true);
    dispatch(
      updateVideoAction(
        status,
        currentQuestionId,
        selectedApplicantDetails?.userId,
        () => {
          setIsVideoActionLoading(false);
          if (status === "APPROVED") {
            toggleApproveModal();
          } else {
            toggleRejectModal();
          }
          dispatch(getApplicantDetails(selectedApplicant));
        
          // Handle rejected videos by removing them from the list
          if (status === "REJECTED") {
          // Remove the rejected video from userVideos
          const updatedUserVideos = userVideos.filter((video: any) => video.questionId !== currentQuestionId);
          
          // If the current active video was rejected, switch to the first remaining video
          if (updatedUserVideos.length > 0) {
            setCurrentActive(0);
            setCurrentQuestionId(updatedUserVideos[0].questionId);
          }
          
          // Update the applicant details in Redux to reflect the removed video
          dispatch({
            type: SET_APPLICANT_DETAILS,
            payload: {
              ...applicantDetails,
              userVideos: updatedUserVideos
            }
          });
          
          // Efficiently update applicants array
          if (updatedUserVideos.length === 0) {
            // Remove the entire applicant if no videos left
            setApplicants((prevApplicants: any[]) => {
              const filteredApplicants = prevApplicants.filter(
                (applicant: any) => applicant.userId !== selectedApplicantDetails?.userId
              );
              
              // Select the first available applicant if current was removed
              if (filteredApplicants.length > 0) {
                setSelectedApplicant(filteredApplicants[0].userId);
                setSelectedApplicantDetails(filteredApplicants[0]);
              }
              
              return filteredApplicants;
            });
          } else {
            // Update only the specific applicant's status
            const overallStatus = determineOverallVideoStatus(updatedUserVideos);
            updateApplicantStatus(selectedApplicantDetails?.userId, { videoStatus: overallStatus });
          }
        } else {
          // Update only the specific applicant's status
          const videoIndex = userVideos.findIndex((video: any) => video.questionId === currentQuestionId);
          if (videoIndex !== -1) {
            const updatedVideos = [...userVideos];
            updatedVideos[videoIndex] = { ...updatedVideos[videoIndex], approvalStatus: status };
            const overallStatus = determineOverallVideoStatus(updatedVideos);
            updateApplicantStatus(selectedApplicantDetails?.userId, { videoStatus: overallStatus });
          }
        }
        },
        () => setIsVideoActionLoading(false)
      )
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#e5e4fe", // Page background color
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* <B2BHeader /> */}

      <Modal open={openApproveModal} onClose={toggleApproveModal}>
        <Box
          sx={{
            ...styles.modalContent,
            width: "400px",
            margin: "auto",
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "500",
              color: "#333",
              marginBottom: "20px",
            }}
          >
            Are you sure you want to approve?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              onClick={toggleApproveModal}
              variant="outlined"
              disabled={isVideoActionLoading}
              sx={{
                // borderColor: "#756DE9",
                color: "#000",
                borderRadius: "50px",
                fontSize: "14px",
                textTransform: "capitalize",
                flex: 1,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#756DE9",
                },
              }}
            >
              No
            </Button>
            <Button
              onClick={() => handleVideoModal("APPROVED")}
              variant="contained"
              disabled={isVideoActionLoading}
              sx={{
                backgroundColor: "#756DE9",
                color: "#fff",
                borderRadius: "50px",
                fontSize: "14px",
                textTransform: "capitalize",
                flex: 1,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#5a50c4",
                },
              }}
            >
              {isVideoActionLoading ? (
                <>
                  <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                  Approving...
                </>
              ) : (
                "Yes"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openRejectModal} onClose={toggleRejectModal}>
        <Box
          sx={{
            ...styles.modalContent,
            width: "400px",
            margin: "auto",
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "500",
              color: "#333",
              marginBottom: "20px",
            }}
          >
            Are you sure you want to reject?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              onClick={toggleRejectModal}
              variant="outlined"
              sx={{
                // borderColor: "#756DE9",
                color: "#000",
                borderRadius: "50px",
                fontSize: "14px",
                textTransform: "capitalize",
                flex: 1,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#756DE9",
                },
              }}
            >
              No
            </Button>
            <Button
              onClick={() => handleVideoModal("REJECTED")}
              variant="contained"
              sx={{
                backgroundColor: "#756DE9",
                color: "#fff",
                borderRadius: "50px",
                fontSize: "14px",
                textTransform: "capitalize",
                flex: 1,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#5a50c4",
                },
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openRetryEnhancementModal} onClose={toggleRetryEnhancementModal}>
        <Box
          sx={{
            ...styles.modalContent,
            width: "420px",
            margin: "auto",
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "500",
              color: "#333",
              marginBottom: "12px",
            }}
          >
            Retry video enhancement?
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#555", mb: 1 }}>
            This will request the enhancement pipeline again for this candidate (all video answers).
          </Typography>
          {phoneNumber ? (
            <Typography sx={{ fontSize: "12px", color: "#777" }}>
              Mobile: {phoneNumber}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "12px", color: "#c62828", mt: 1 }}>
              Phone number is missing for this profile.
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              onClick={toggleRetryEnhancementModal}
              variant="outlined"
              disabled={isRetryEnhancementLoading}
              sx={{
                color: "#000",
                borderRadius: "50px",
                fontSize: "14px",
                textTransform: "capitalize",
                flex: 1,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#756DE9",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRetryVideoEnhancement}
              variant="contained"
              disabled={isRetryEnhancementLoading || !phoneNumber}
              sx={{
                backgroundColor: "#756DE9",
                color: "#fff",
                borderRadius: "50px",
                fontSize: "14px",
                textTransform: "capitalize",
                flex: 1,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#5a50c4",
                },
              }}
            >
              {isRetryEnhancementLoading ? (
                <>
                  <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                  Retrying...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "top",
          width: "100%",
          paddingX: 2, // Explicit padding shorthand
          paddingTop: 2, // Optional: Add top padding if required
          boxSizing: "border-box", // Ensures padding is included in the width
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", width: "20%", padding: "20px" }}>
          <Tabs
            value={applicantsSectionTab}
            onChange={(_, newValue: number) => setApplicantsSectionTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              marginBottom: 2,
              "& .MuiTab-root": { minHeight: 36, fontSize: "13px", fontWeight: "500", textTransform: "none" },
              "& .Mui-selected": { color: "#756de9" },
              "& .MuiTabs-indicator": { backgroundColor: "#756de9" },
            }}
          >
            <Tab label="Pending" />
            <Tab label="Approved" />
          </Tabs>

          {/* Search Input */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search applicants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              marginBottom: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                "& fieldset": {
                  borderColor: "#D0D0D0",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#A0A0A0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#756DE9",
                  borderWidth: "1px",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img src="/images/search.svg" width="16px" height="16px" alt="Search" />
                </InputAdornment>
              ),
            }}
          />

          {loadingApplicants ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                paddingRight: "10px",
              }}
              id="scrollable-container"
            >
              <InfiniteScroll
                dataLength={applicants.length}
                next={() => fetchApplicants(true, false, false, debouncedSearchTerm, videoStatusFilter)}
                hasMore={hasMoreApplicants}
                loader={
                  <Box
                    sx={{
                      textAlign: "center",
                      mt: 2,
                      fontSize: "12px",
                    }}
                  >
                    Loading...
                  </Box>
                }
                endMessage={
                  <Box component="p" sx={{ textAlign: "center", mt: 2, fontSize: "12px" }}>
                    <b></b>
                  </Box>
                }
                scrollableTarget="scrollable-container"
              >
                {applicants?.length > 0 ? (
                  applicants?.map((el: any, idx: number) => {
                  const isSelected = selectedApplicant === el?.userId;

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        padding: "10px",
                        borderRadius: "10px",
                        border: isSelected ? "2px solid #4B44D4" : "1px solid #756DE9",
                        marginBottom: "10px",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#E6E6F5" : "transparent",
                        boxShadow: isSelected ? "0px 0px 10px rgba(0, 0, 0, 0.1)" : "none",
                        "&:hover": {
                          backgroundColor: isSelected ? "#D2D2E6" : "#f0f0f0",
                        },
                      }}
                      onClick={() => handleSelectApplicant(el?.userId, el)}
                    >
                      <img src={el?.profileImage} width="50px" height="50px" />
                      <Box sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "#000105", fontWeight: "400" }}>
                          {el?.fullName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#B56E00",
                            fontWeight: "600",
                            backgroundColor: "#EDCD5A5E",
                            padding: "2px 5px",
                            borderRadius: "100px",
                          }}
                        >
                          {el?.videoStatus}
                        </Typography>
                      </Box>
                    </Box>
                  );
                  })
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#777777",
                      fontSize: "14px",
                    }}
                  >
                    {searchTerm
                      ? "No applicants found matching your search."
                      : applicantsSectionTab === 0
                        ? "No pending applicants."
                        : "No approved applicants."}
                  </Box>
                )}
              </InfiniteScroll>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", flex: 1, position: "relative", minWidth: 0 }}>
          <Box sx={{ width: "40%", padding: "20px" }}>
          {applicantDetailsLoading ? (
            <>
              {/* Video section shimmer */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "10px",
                  borderRadius: "10px",
                  height: "90px",
                  backgroundColor: "#E8E7F5",
                }}
              >
                <Skeleton animation="wave" variant="circular" width={50} height={50} sx={{ flexShrink: 0 }} />
                <Box sx={{ marginLeft: "12px", flex: 1 }}>
                  <Skeleton animation="wave" variant="text" width="60%" sx={{ fontSize: "18px" }} />
                  <Skeleton animation="wave" variant="text" width="40%" sx={{ fontSize: "12px", mt: 0.5 }} />
                </Box>
              </Box>
              <Box sx={{ backgroundColor: "#fff", mt: 0 }}>
                <Skeleton animation="wave" variant="rectangular" height={400} sx={{ borderRadius: 0 }} />
                <Box sx={{ display: "flex", gap: 1, my: 1 }}>
                  <Skeleton animation="wave" variant="rectangular" height={40} sx={{ flex: 1, borderRadius: "6px" }} />
                  <Skeleton animation="wave" variant="rectangular" height={40} sx={{ flex: 1, borderRadius: "6px" }} />
                </Box>
                <Divider variant="middle" />
                {[1, 2].map((i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                    <Skeleton animation="wave" variant="rectangular" width="33%" height={100} sx={{ borderRadius: "5px" }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton animation="wave" variant="text" width="80%" />
                      <Skeleton animation="wave" variant="text" width="60%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              backgroundColor: "#756DE9",
              padding: "10px",
              borderTopRightRadius: "10px",
              borderTopLeftRadius: "10px",
              height: "90px",
            }}
          >
            <img src={profileImage} width="50px" height="50px"></img>
            <Grid sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
              <Typography sx={{ fontSize: "18px", color: "#fff", fontWeight: "500" }}>{fullName}</Typography>
              <Typography sx={{ fontSize: "12px", color: "#fff", fontWeight: "400" }}>
                {educationDetails?.some((el: any) => el?.yetToGraduate) ? "Studying Currently" : null}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ backgroundColor: "#fff" }}>
            {userVideos?.length
              ? userVideos?.map((el: any, index: number) => {
                  if (currentActive === index) {
                    const statusDisplay = getVideoStatusDisplay(el);
                    const StatusIcon = statusDisplay.Icon;
                    return (
                      <React.Fragment key={index}>
                        <Grid>
                          <ReactPlayer height={400} url={el?.videoPath} width="100%" controls playing={false} />
                        </Grid>

                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            borderRadius: "99px",
                            backgroundColor: statusDisplay.bg,
                            mt: 1,
                          }}
                        >
                          <StatusIcon sx={{ fontSize: 14, color: statusDisplay.color }} />
                          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: statusDisplay.color }}>
                            {statusDisplay.label}
                          </Typography>
                        </Box>

                        {userVideos?.length - 1 === index ? null : <Divider variant="middle" />}

                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "space-between",
                            my: 1,
                          }}
                        >
                          <Button
                            sx={{
                              flex: 1,
                              backgroundColor: "#4CAF50",
                              color: "#fff",
                              fontSize: "14px",
                              fontWeight: "500",
                              height: "40px",
                              borderRadius: "6px",
                              "&:hover": {
                                backgroundColor: "#45A049",
                              },
                              marginRight: "5px",
                              opacity: el?.approvalStatus === "APPROVED" ? 0.5 : 1,
                              cursor: el?.approvalStatus === "APPROVED" ? "not-allowed" : "pointer",
                            }}
                            onClick={toggleApproveModal}
                            disabled={el?.approvalStatus === "APPROVED"}
                          >
                            Approve
                          </Button>

                          <Button
                            sx={{
                              flex: 1,
                              backgroundColor: "#F44336",
                              color: "#fff",
                              fontSize: "14px",
                              fontWeight: "500",
                              height: "40px",
                              borderRadius: "6px",
                              "&:hover": {
                                backgroundColor: "#D32F2F",
                              },
                              marginLeft: "5px",
                              opacity: el?.approvalStatus === "APPROVED" ? 0.5 : 1,
                              cursor: el?.approvalStatus === "APPROVED" ? "not-allowed" : "pointer",
                            }}
                            onClick={toggleRejectModal}
                            disabled={el?.approvalStatus === "APPROVED"}
                          >
                            Reject
                          </Button>
                        </Box>
                      </React.Fragment>
                    );
                  }
                  const statusDisplay = getVideoStatusDisplay(el);
                  const StatusIcon = statusDisplay.Icon;
                  return (
                    <React.Fragment key={index}>
                      <Grid
                        onClick={() => handleVideoClick(index, el?.questionId)}
                        container
                        spacing={2}
                        alignItems="center"
                        mb={2}
                      >
                        <Grid item xs={4}>
                          <div className="position-relative">
                            <VideoThumbnail videoUrl={el?.videoPath} width="100%" height="150px" />
                          </div>
                        </Grid>
                        <Grid item xs={6}>
                          <div className="position-relative">
                            <Typography className="custom-typography-chat">{el?.topic}</Typography>
                            <Typography style={{ fontSize: "12px" }}>{el?.question}</Typography>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: "99px",
                                backgroundColor: statusDisplay.bg,
                                mt: 0.5,
                              }}
                            >
                              <StatusIcon sx={{ fontSize: 12, color: statusDisplay.color }} />
                              <Typography sx={{ fontSize: "10px", fontWeight: 600, color: statusDisplay.color }}>
                                {statusDisplay.label}
                              </Typography>
                            </Box>
                          </div>
                        </Grid>
                        {/* <Grid item xs={2} sx={{ pt: "0px !important" }}>
                          <VideoMenu />
                        </Grid> */}
                      </Grid>

                      {userVideos?.length - 1 === index ? null : <Divider variant="middle" />}
                    </React.Fragment>
                  );
                })
              : null}
            {userVideos?.length ? (
              <Box sx={{ px: 2, pb: 2, pt: 1.5, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setOpenRetryEnhancementModal(true)}
                  disabled={!phoneNumber}
                  sx={{
                    borderColor: "#756DE9",
                    color: "#756DE9",
                    fontSize: "14px",
                    fontWeight: "500",
                    height: "40px",
                    borderRadius: "6px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#5a50c4",
                      backgroundColor: "rgba(117, 109, 233, 0.06)",
                    },
                    "&.Mui-disabled": {
                      borderColor: "rgba(117, 109, 233, 0.35)",
                      color: "rgba(0,0,0,0.38)",
                    },
                  }}
                >
                  Retry video enhancement (all answers)
                </Button>
                <Typography sx={{ fontSize: "11px", color: "#777", mt: 1, textAlign: "center", lineHeight: 1.4 }}>
                  Applies to every video answer below for {fullName || "this candidate"}.
                </Typography>
              </Box>
            ) : null}
          </Box>
            </>
          )}
        </Box>

        <Box sx={{ height: "100%", width: "50%", margin: "20px", border: "1px solid #756de9", borderRadius: "10px" }}>
          {applicantDetailsLoading ? (
            <>
              {/* Profile details section shimmer */}
              <Box sx={{ px: 2, pt: 2 }}>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Skeleton animation="wave" variant="rounded" width="33%" height={36} sx={{ borderRadius: "4px" }} />
                  <Skeleton animation="wave" variant="rounded" width="33%" height={36} sx={{ borderRadius: "4px" }} />
                  <Skeleton animation="wave" variant="rounded" width="33%" height={36} sx={{ borderRadius: "4px" }} />
                </Box>
              </Box>
              <Box sx={{ padding: "20px" }}>
                <Skeleton animation="wave" variant="text" width={120} sx={{ fontSize: "14px", mb: 2 }} />
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Skeleton animation="wave" variant="rounded" width="33%" height={60} />
                  <Skeleton animation="wave" variant="rounded" width="33%" height={60} />
                </Box>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Skeleton animation="wave" variant="rounded" width="50%" height={60} />
                  <Skeleton animation="wave" variant="rounded" width="50%" height={60} />
                </Box>
                <Skeleton animation="wave" variant="text" width={100} sx={{ fontSize: "14px", mt: 3, mb: 2 }} />
                <Grid container spacing={2}>
                  {[1, 2].map((i) => (
                    <Grid item xs={6} key={i}>
                      <Skeleton animation="wave" variant="rounded" height={80} sx={{ borderRadius: "5px" }} />
                    </Grid>
                  ))}
                </Grid>
                <Skeleton animation="wave" variant="text" width={90} sx={{ fontSize: "14px", mt: 3, mb: 2 }} />
                <Skeleton animation="wave" variant="rounded" height={100} sx={{ borderRadius: "5px", mb: 1 }} />
                <Skeleton animation="wave" variant="rounded" height={100} sx={{ borderRadius: "5px" }} />
              </Box>
            </>
          ) : (
            <>
          <Tabs
            value={activeTab1}
            onChange={handleTabChange1}
            TabIndicatorProps={{
              style: { backgroundColor: "#756de9", height: "4px" }, // Adjust this as needed
            }}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontSize: "14px",
                fontWeight: "500",
                color: "#000",
              },
              "& .Mui-selected": {
                color: "#756de9", // Change color of the selected tab text
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#756de9 !important", // Ensure the indicator is visible
              },
            }}
          >
            <Tab label="PROFILE DETAILS" sx={{ fontSize: "14px", weight: "500", color: "#000" }} />
            <Tab label="SPECIALIZATION" sx={{ fontSize: "14px", weight: "500", color: "#000" }} />
            <Tab label="PERSONALITY" sx={{ fontSize: "14px", weight: "500", color: "#000" }} />
          </Tabs>

          {/* Profile Details Content */}
          {activeTab1 === 0 && (
            <Box
              sx={{
                backgroundColor: "#fff",
                padding: "20px",
                textAlign: "center",
                borderRadius: "10px",
              }}
            >
              <Box style={{ padding: "2px", marginBottom: "10px" }} className="">
                <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550", textAlign: "left" }}>
                  Demographics
                </Typography>

                <Stack style={{ marginTop: "10px" }} direction="column" spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ border: "1px solid #C4C4C44D", borderRadius: "5px", padding: "10px", width: "33%" }}
                    >
                      <img src="/images/dob.png" width="20px"></img>
                      <Typography style={{ color: "#222222", fontSize: "14px", fontWeight: "400", textAlign: "left" }}>
                        {moment(dob).format("DD/MM/YYYY")}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ border: "1px solid #C4C4C44D", borderRadius: "5px", padding: "10px", width: "33%" }}
                    >
                      <img src="/images/location.png" width="20px"></img>
                      <Typography style={{ color: "#222222", fontSize: "14px", fontWeight: "400", textAlign: "left" }}>
                        {city}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ border: "1px solid #C4C4C44D", borderRadius: "5px", padding: "10px", width: "50%" }}
                    >
                      <img src="/images/language.png" width="20px"></img>
                      <Typography style={{ color: "#222222", fontSize: "14px", fontWeight: "400", textAlign: "left" }}>
                        {languages?.join(", ")}
                      </Typography>
                    </Stack>
                    {userExperience?.length > 1 ? (
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{ border: "1px solid #C4C4C44D", borderRadius: "5px", padding: "10px", width: "50%" }}
                      >
                        <img src="/images/client-website/id-image.png" width="20px"></img>
                        <Typography
                          style={{ color: "#222222", fontSize: "14px", fontWeight: "400", textAlign: "left" }}
                        >
                          Currently Working
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
              <Box style={{ padding: "2px", marginBottom: "10px" }} className="">
                <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550", textAlign: "left" }}>
                  Education
                </Typography>

                <Grid container spacing={2} style={{ marginTop: "-5px" }}>
                  {educationDetails?.map((el: any, id: any) => {
                    return (
                      <Grid item xs={6} key={id}>
                        <Stack
                          direction="column"
                          spacing={0.5}
                          sx={{
                            border: "1px solid #C4C4C44D",
                            borderRadius: "5px",
                            padding: "10px",
                          }}
                        >
                          <Typography
                            style={{ color: "#222222", fontSize: "14px", fontWeight: "500", textAlign: "left" }}
                          >
                            {el?.educationType === "Board" ? el?.board : el?.courseName}
                          </Typography>
                          <Typography
                            style={{ color: "#1F2235", fontSize: "12px", fontWeight: "500", textAlign: "left" }}
                          >
                            {el?.educationType === "Board" ? el?.schoolName : el?.institutionName}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <img src="/images/Calendar-new.png" width="20px" />
                            <Typography
                              style={{ color: "#555555", fontSize: "12px", fontWeight: "400", textAlign: "left" }}
                            >
                              {el?.yetToGraduate
                                ? `Studying since ${el?.startDate}`
                                : el?.startDate &&
                                  `${calculateYears(el.startDate, el.endDate)}, ${el.startDate} - ${
                                    el.endDate || "Present"
                                  }`}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              <Box style={{ padding: "2px", marginBottom: "10px" }} className="">
                <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550", textAlign: "left" }}>
                  Experiences
                </Typography>
                {userExperience?.map((el: any, id: any) => {
                  const experienceWorkLink = pickExperienceWorkLink(el);
                  return (
                    <Stack key={id} style={{ marginTop: "10px" }} direction="column" spacing={1}>
                      <Stack direction="row" spacing={1}>
                        <Stack
                          direction="column"
                          spacing={0.5}
                          sx={{ border: "1px solid #C4C4C44D", borderRadius: "5px", padding: "10px", width: "100%" }}
                        >
                          <Typography
                            style={{ color: "#222222", fontSize: "14px", fontWeight: "500", textAlign: "left" }}
                          >
                            {el?.companyName}
                          </Typography>
                          <Typography
                            style={{ color: "#1F2235", fontSize: "12px", fontWeight: "500", textAlign: "left" }}
                          >
                            {el?.designationName}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <img src="/images/client-website/people-icon.png" width="20px"></img>
                            <Typography
                              style={{ color: "#555555", fontSize: "12px", fontWeight: "400", textAlign: "left" }}
                            >
                              {el?.departmentName}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <img src="/images/Calendar-new.png" width="20px"></img>
                            <Typography
                              style={{ color: "#555555", fontSize: "12px", fontWeight: "400", textAlign: "left" }}
                            >
                              {!el?.endDate
                                ? `${moment(el?.startDate).format("DD/MM/YYYY")} - Currently Working Here`
                                : `${moment(el?.startDate).format("DD/MM/YYYY")} - ${moment(el?.endDate).format(
                                    "DD/MM/YYYY"
                                  )}`}
                            </Typography>
                          </Stack>
                          {experienceWorkLink ? (
                            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
                              <Box
                                component="img"
                                src="/images/link.png"
                                alt=""
                                sx={{
                                  width: 20,
                                  height: 20,
                                  flexShrink: 0,
                                  mt: "2px",
                                  objectFit: "contain",
                                }}
                              />
                              <Box
                                component="a"
                                href={formatWorkLinkHref(experienceWorkLink)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  minWidth: 0,
                                  flex: 1,
                                  color: "#1976d2",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  lineHeight: 1.43,
                                  textDecoration: "underline",
                                  textAlign: "left",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {experienceWorkLink}
                              </Box>
                            </Stack>
                          ) : null}
                          <Typography
                            style={{ color: "#555555", fontSize: "12px", fontWeight: "400", textAlign: "left" }}
                          >
                            {el?.description}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Specialization Content */}
          {activeTab1 === 1 && (
            <Box style={{ borderRadius: "10px", padding: "20px", marginBottom: "10px" }} className="">
              <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>Skill Set</Typography>

              <Stack direction="column" marginBottom="20px">
                <Typography style={{ color: "#555555", fontSize: "14px" }}>
                  {skills?.map((tool: string, index: number) => (
                    <React.Fragment key={index}>
                      <span>{tool}</span>
                      {index !== skills.length - 1 && <span style={{ color: "orange" }}> | </span>}
                    </React.Fragment>
                  ))}
                </Typography>

                <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550", margin: "10px 0px" }}>
                  Tools Known
                </Typography>

                <Typography style={{ color: "#555555", fontSize: "14px" }}>
                  {tools?.map((tool: string, index: number) => (
                    <React.Fragment key={index}>
                      <span>{tool}</span>
                      {index !== tools.length - 1 && <span style={{ color: "orange" }}> | </span>}
                    </React.Fragment>
                  ))}
                </Typography>

                <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550", margin: "10px 0px 4px" }}>
                  Work link
                </Typography>
                {resolvedWorkLinks.length ? (
                  <Stack direction="column" spacing={0.75}>
                    {resolvedWorkLinks.map((wl: string, i: number) => (
                      <Link
                        key={`${wl}-${i}`}
                        href={formatWorkLinkHref(wl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: "14px", wordBreak: "break-all" }}
                      >
                        {wl}
                      </Link>
                    ))}
                  </Stack>
                ) : (
                  <Typography style={{ color: "#999999", fontSize: "14px", fontStyle: "italic" }}>Not added</Typography>
                )}
              </Stack>

              <Stack
                style={{
                  backgroundColor: "#e5e3fd",
                  height: "20px",
                  padding: "10px",
                  borderRadius: "10px 10px 0px 0px",
                }}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                  Certifications
                </Typography>
              </Stack>
              <Stack style={{ padding: "10px" }} direction="column">
                <Stack style={{ padding: "10px" }} direction="column">
                  {userCertificates?.map((el: any, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <Typography
                          style={{
                            color: "#000000",
                            fontSize: "14px",
                            fontWeight: "550",
                            margin: "10px 0px",
                          }}
                        >
                          {el?.certificateName}
                        </Typography>

                        <Stack direction="column" spacing={1}>
                          <Stack direction="row" spacing={1}>
                            <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                              {el?.institute}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <img src="/images/Calendar-new.png" width="20px"></img>
                            <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                              Duration: {el?.courseDuration && formatDuration(el.courseDuration)}
                            </Typography>
                          </Stack>
                          {el?.courseExpiry ? (
                            <Stack direction="row" spacing={1}>
                              <img src="/images/complete-profile/Course Expiry Date.svg" width="20px"></img>
                              <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                Certification Expiry Date: {moment(el?.courseExpiry)?.format("DD/MM/YYYY")}
                              </Typography>
                            </Stack>
                          ) : null}

                          {el?.certificateReceived && (
                            <Stack direction="row" spacing={1}>
                              <img src="/images/Tick.png" width="20px"></img>
                              <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                Received Certificate
                              </Typography>
                            </Stack>
                          )}
                          {index < userCertificates.length - 1 && <Divider sx={{ mt: 2, mb: 1 }} variant="middle" />}
                        </Stack>
                      </React.Fragment>
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          )}

          {activeTab1 === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "left", padding: "20px" }}>
              <img src="/images/connector-image.png" alt="Connector" width="150px" />
              <Typography style={{ fontSize: "18px", fontWeight: "600", color: "#1F2235", marginTop: "10px" }}>
                {userPersonality?.map((el: any) => el?.displayPersonalityType).join(", ")}
              </Typography>
              <Typography style={{ fontSize: "14px", fontWeight: "400", color: "#1F2235", marginTop: "10px" }}>
                {userPersonality
                  ?.map((el: any) => el?.typeDescriptors)
                  .join("\n")
                  .split("\n")
                  .map((line: any, index: any) => (
                    <span key={index}>
                      {line}
                      <br />
                    </span>
                  ))}
              </Typography>

              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#756DE9",
                  cursor: "pointer",
                  gap: 1,
                  mt: 2,
                }}
                onClick={() => window.open(`${ROUTE_PERSONALITY_PROFILER}?applicant_id=${selectedApplicant}`, "_blank")}
              >
                View Report <ArrowForward sx={{ fontSize: "18px" }} />
              </Typography>

              <Divider sx={{ marginTop: "20px", marginBottom: "120px" }}></Divider>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#1F2235",
              borderRadius: "0px 0px 10px 10px",
              marginTop: "auto", // Ensures this box stays at the bottom of the flex container
            }}
          >
            <Button
              onClick={handleOpenModal}
              sx={{ fontSize: "14px", weight: "400", color: "#fff", textAlign: "center", width: "50%" }}
            >
              Reach Out{" "}
              <img
                src="/images/client-website/reach-out.png"
                width="15px"
                height="15px"
                style={{ marginLeft: "8px" }}
              ></img>
            </Button>

            {/* Modal Component */}
            <Modal open={openModal} onClose={handleCloseModal}>
              <Box sx={styles.modalContent}>
                <Typography variant="h6" component="h2">
                  Reach out to the candidate
                </Typography>
                <Grid
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    border: "1px solid #756DE9",
                    borderRadius: "10px",
                    alignItems: "center",
                    marginTop: "10px",
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "row", padding: "10px", borderRadius: "10px", width: "50%" }}
                  >
                    <Grid sx={{ display: "flex", flexDirection: "row", padding: "10px", borderRadius: "10px" }}>
                      <img src={selectedApplicantDetails?.profilePicturePath} width="50px" height="50px"></img>
                      <Grid sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "#000105", fontWeight: "400" }}>
                          {selectedApplicantDetails?.fullName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#B56E00",
                            fontWeight: "600",
                            backgroundColor: "#EDCD5A5E;",
                            padding: "2px 5px",
                            borderRadius: "100px",
                          }}
                        >
                          {selectedApplicantDetails?.applicationStatus}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignContent: "center", gap: 1 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <img src="/images/Call-icon.png" width="20"></img>
                        <a style={{ textDecoration: "none", color: "#756DE9" }} href={`tel:${phoneNumber}`}>
                          {phoneNumber}
                        </a>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <img src="/images/Email_icon.png" width="20" height="18"></img>
                        <a
                          style={{ textDecoration: "none", color: "#756DE9", fontSize: "16px", fontWeight: "400" }}
                          href={`mailto:${email}`}
                        >
                          {email}
                        </a>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Divider sx={{ marginY: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                  <Button
                    onClick={handleCloseModal}
                    variant="contained"
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "99px",
                      fontSize: "16px",
                      textTransform: "capitalize",
                      width: "12%",
                      color: "#000000",
                      padding: "10px 50px",
                      "&:hover ": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleCloseModal()}
                    sx={{
                      backgroundColor: "#756DE9",
                      color: "#fff",
                      borderRadius: "50px",
                      padding: "10px 40px",
                      "&:hover": {
                        backgroundColor: "#756DE9", // Keep the same background color on hover
                      },
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Modal>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ marginLeft: 2, marginRight: 2, height: "24px", backgroundColor: "#ffffff", marginTop: "6px" }}
            />
            <Button
              onClick={handleOpenModalDecline}
              sx={{ fontSize: "14px", weight: "400", color: "#FD794B", textAlign: "center", width: "50%" }}
            >
              Decline{" "}
              <img
                src="/images/client-website/reject.png"
                width="12px"
                height="12px"
                style={{ marginLeft: "8px" }}
              ></img>
            </Button>

            {/* Modal Component */}
            <Modal open={openModalDecline} onClose={handleCloseModalDecline}>
              <Box sx={styles.modalContentDecline}>
                <Typography variant="h6" component="h2">
                  Declined a candidate
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid #756DE9",
                  }}
                >
                  <Grid sx={{ display: "flex", flexDirection: "row", padding: "10px", borderRadius: "10px" }}>
                    <img src={selectedApplicantDetails?.profilePicturePath} width="50px" height="50px"></img>
                    <Grid sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
                      <Typography sx={{ fontSize: "13px", color: "#000105", fontWeight: "400" }}>
                        {selectedApplicantDetails?.fullName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#B56E00",
                          fontWeight: "600",
                          backgroundColor: "#EDCD5A5E;",
                          padding: "2px 5px",
                          borderRadius: "100px",
                        }}
                      >
                        {selectedApplicantDetails?.applicationStatus}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ marginY: 2 }} />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                  <Button
                    onClick={handleCloseModalDecline}
                    variant="contained"
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "99px",
                      fontSize: "16px",
                      textTransform: "capitalize",
                      width: "12%",
                      color: "#000000",
                      padding: "10px 50px",
                      "&:hover ": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleJobAction("DECLINED")}
                    sx={{
                      backgroundColor: "#756DE9",
                      color: "#fff",
                      borderRadius: "50px",
                      padding: "10px 40px",
                      "&:hover": {
                        backgroundColor: "#756DE9", // Keep the same background color on hover
                      },
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Modal>
          </Box>
            </>
          )}
        </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoDetails;
